import { Archive, ArrowLeft, Loader2, MessageSquare, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import StarsIcon from "@/assets/icons/stars-icon.svg?react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/AlertDialog";

import { useArchiveChat } from "../services/useArchiveChat";
import { useChats } from "../services/useChats";
import { ChatListItem } from "../types/chat";

function formatChatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return "Bugun";
  if (days === 1) return "Kecha";
  if (days < 7) return `${days} kun oldin`;
  return date.toLocaleDateString();
}

function groupChatsByDate(chats: ChatListItem[]) {
  const groups: { date: string; chats: ChatListItem[] }[] = [];
  chats.forEach((chat) => {
    const dateLabel = formatChatDate(chat.created_at);
    const existing = groups.find((g) => g.date === dateLabel);
    if (existing) {
      existing.chats.push(chat);
    } else {
      groups.push({ date: dateLabel, chats: [chat] });
    }
  });
  return groups;
}

export const AIChatSidebar = () => {
  const { t } = useTranslation("layout");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeChatUuid = searchParams.get("chat");

  const { data: chats, isLoading } = useChats();
  const archiveMutation = useArchiveChat();

  const [archiveTarget, setArchiveTarget] = useState<ChatListItem | null>(null);

  const handleSelectChat = (uuid: string) => {
    setSearchParams({ chat: uuid });
  };

  const handleNewChat = () => {
    setSearchParams({});
  };

  const handleArchive = () => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.uuid, {
      onSuccess: () => {
        if (activeChatUuid === archiveTarget.uuid) {
          setSearchParams({});
        }
        setArchiveTarget(null);
      },
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-[80px] items-center gap-3 border-b border-border-secondary px-4 py-3">
        <Link
          to="/dashboard"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-secondary transition-colors hover:bg-bg-tertiary"
          aria-label={t("ai.back")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-400">
            <StarsIcon className="h-4 w-4" />
          </div>
          <span className="text-sm font-semibold text-text-primary">
            {t("ai.title")}
          </span>
        </div>
      </div>

      <div className="px-3 py-3">
        <button
          type="button"
          onClick={handleNewChat}
          className="flex w-full items-center gap-2 rounded-lg border border-border-secondary px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-secondary"
        >
          <Plus className="h-4 w-4" />
          {t("ai.newChat")}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-fg-quaternary" />
          </div>
        )}
        {chats &&
          groupChatsByDate(chats).map((group) => (
            <div key={group.date} className="mb-4">
              <p className="mb-1 px-2 text-xs font-medium text-text-placeholder">
                {group.date}
              </p>
              <div className="space-y-0.5">
                {group.chats.map((chat) => (
                  <div
                    key={chat.uuid}
                    className={`group/chat flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${
                      activeChatUuid === chat.uuid
                        ? "bg-bg-tertiary text-text-primary"
                        : "text-text-secondary hover:bg-bg-tertiary"
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectChat(chat.uuid)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSelectChat(chat.uuid);
                    }}
                  >
                    <MessageSquare className="h-4 w-4 shrink-0 text-fg-quaternary" />
                    <span className="min-w-0 flex-1 truncate">
                      {chat.title}
                    </span>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-fg-quaternary opacity-0 transition-opacity hover:text-fg-secondary group-hover/chat:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setArchiveTarget(chat);
                      }}
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>

      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("ai.archiveTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("ai.archiveDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ai.archiveCancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("ai.archiveConfirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
